import { getLocale } from '@/hooks/helpers'
import { format } from 'date-fns'
import type { TFunction } from 'i18next'
import { type CampaignRow, periodLabel, targetAreaLabel } from './breakdown-format'

// Keep in sync with FormSubmissionsExport::escapeFormula (backend).
const FORMULA_START = /^[\t\r\n]|^\s*[=+\-@＝＋－＠]/

export function buildBreakdownCsv(
	rows: CampaignRow[],
	{ t, locale = getLocale() }: { t: TFunction; locale?: string },
): string {
	const decimalMark = new Intl.NumberFormat(locale).formatToParts(1.1).find((part) => part.type === 'decimal')?.value
		?? '.'
	const delimiter = decimalMark === ',' ? ';' : ','
	const label = (key: string) => t(`organisationAnalytics.breakdown.${key}`)
	const money = (cents: number) => (cents / 100).toFixed(2).replace('.', decimalMark)

	const columns: [string, (row: CampaignRow) => string][] = [
		[label('campaign'), (row) => row.title],
		[label('status'), (row) => t(`breakdown.status.${row.status}`)],
		[label('location'), (row) => row.location ?? ''],
		[label('targetArea'), (row) => targetAreaLabel(row) ?? ''],
		[label('period'), (row) => row.isEvergreen ? label('ongoing') : periodLabel(row) ?? ''],
		[label('views'), (row) => String(row.funnel.views)],
		[label('clicks'), (row) => String(row.funnel.clicks)],
		[label('candidates'), (row) => String(row.funnel.candidates)],
		[`${label('excellent')} (89-100)`, (row) => String(row.scoreBands.excellent)],
		[`${label('good')} (66-88)`, (row) => String(row.scoreBands.good)],
		[`${label('noMatch')} (0-65)`, (row) => String(row.scoreBands.bad)],
		[label('new'), (row) => String(row.pipeline.new)],
		[label('other'), (row) => String(row.pipeline.other)],
		[label('rejected'), (row) => String(row.pipeline.rejected)],
		[label('hired'), (row) => String(row.pipeline.hired)],
		[`${label('budget')} (EUR)`, (row) => money(row.budget)],
		[
			`${label('costPerCandidate')} (EUR)`,
			(row) => row.costPerCandidate !== null ? money(row.costPerCandidate) : '',
		],
	]

	const cell = (value: string) => {
		const safe = FORMULA_START.test(value) ? `'${value}` : value
		return safe.includes(delimiter) || /["\r\n]/.test(safe)
			? `"${safe.replaceAll('"', '""')}"`
			: safe
	}

	const lines = [
		columns.map(([header]) => header),
		...rows.map((row) => columns.map(([, value]) => value(row))),
	]

	return '﻿' + lines.map((line) => line.map(cell).join(delimiter)).join('\r\n') + '\r\n'
}

export function breakdownCsvFilename(date: Date): string {
	return `analytics-${format(date, 'yyyy-MM-dd')}.csv`
}
